import { intersection } from "lodash";
import { AllowedBlockchains } from "../../../chain/models/internal";
import { defaultFieldNames, DefaultFieldNames } from "../default-fields";
import { Between } from "./between";
export namespace Field {
  interface BasicField<T extends string> {
    field: T;
    title: Record<string, string>;
    desc?: Record<string, string> | null;
    optional?: boolean;
    type?: string;
  }

  export type Select<T extends string> = BasicField<T> & {
    type: "DROP_DOWN";
    typeDetails: { availableOptions: Record<string, Array<string>> };
  };

  export type MultiSelect<T extends string> = BasicField<T> & {
    type: "MULTI_SELECT";
    typeDetails: { availableOptions: Record<string, Array<string>> };
  };

  export type MultiCheckbox<T extends string> = BasicField<T> & {
    type: "MULTI_CHECKBOX";
    typeDetails: { availableOptions: Record<string, Array<string>> };
  };

  export type Switch<T extends string> = BasicField<T> & {
    type: "SWITCH";
    typeDetails: { availableOption?: Record<string, string> };
  };

  export type Radio<T extends string> = BasicField<T> & {
    type: "RADIO";
    typeDetails: { availableOptions: Record<string, Array<string>> } | null;
  };

  export type Text<T extends string> = BasicField<T> & {
    type: "TEXT";
    typeDetails: {
      minCharacters: number;
      maxCharacters: number;
      pattern: string | null;
    } | null;
  };

  export type Date<T extends string> = BasicField<T> & {
    type: "DATE";
    typeDetails: {
      between: Between.T;
    } | null;
  };

  export type Number<T extends string> = BasicField<T> & {
    type: "NUMBER";
    typeDetails: {
      min: number;
      max: number;
    } | null;
  };

  export type File<T extends string> = BasicField<T> & {
    type: "FILE_UPLOAD";
    typeDetails: {
      fileSize: string; // Not used yet
    } | null;
  };

  export function isSelectField<T extends string>(f: Field<T>): f is Select<T> {
    return (f as any).type === "DROP_DOWN";
  }

  export function isSwitchField<T extends string>(f: Field<T>): f is Switch<T> {
    return (f as any).type === "SWITCH";
  }

  export function isMultiSelectField<T extends string>(
    f: Field<T>
  ): f is MultiSelect<T> {
    return (f as any).type === "MULTI_SELECT";
  }

  export function isMultiCheckboxField<T extends string>(
    f: Field<T>
  ): f is MultiCheckbox<T> {
    return (f as any).type === "MULTI_CHECKBOX";
  }

  export function isRadioField<T extends string>(f: Field<T>): f is Radio<T> {
    return (f as any).type === "RADIO";
  }

  export function isTextField<T extends string>(f: Field<T>): f is Text<T> {
    return (f as any).type === "TEXT";
  }

  export function isDateField<T extends string>(f: Field<T>): f is Number<T> {
    return (f as any).type === "DATE";
  }

  export function isNumberField<T extends string>(f: Field<T>): f is Number<T> {
    return (f as any).type === "NUMBER";
  }

  export function isFileField<T extends string>(f: Field<T>): f is File<T> {
    return (f as any).type === "FILE_UPLOAD";
  }

  export type AddressAttachmentField = Select<"addressAttachment"> & {
    field: "addressAttachment";
  };

  export function isAddressAttachmentField(
    f: Field<string>
  ): f is AddressAttachmentField {
    return isSelectField(f) && f.field === "addressAttachment";
  }

  export type SourceWalletField = Omit<
    Select<"sourceWallet">,
    "typeDetails" | "field"
  > & {
    field: "sourceWallet";
    typeDetails: {
      availableOptions: Record<string, Array<AllowedBlockchains>>;
    };
  };

  export function isSourceWalletField(
    f: Field<string>
  ): f is SourceWalletField {
    return isSelectField(f) && f.field === "sourceWallet";
  }

  export type DestinationWalletField = Omit<
    Select<"destinationWallet">,
    "typeDetails" | "field"
  > & {
    field: "destinationWallet";
    typeDetails: {
      availableOptions: Record<string, Array<AllowedBlockchains>>;
    };
  };

  export function isDestinationWalletField(
    f: Field<string>
  ): f is DestinationWalletField {
    return isSelectField(f) && f.field === "destinationWallet";
  }

  export type PledgeField = Omit<BasicField<"pledge">, "field"> & {
    field: "pledge";
  };

  export function isPledgeField(f: Field<string>): f is PledgeField {
    return f.field === "pledge";
  }

  export type StakingWalletField = Omit<
    Select<"stakingWallet">,
    "typeDetails" | "field"
  > & {
    field: "stakingWallet";
    typeDetails: {
      availableOptions: Record<string, Array<AllowedBlockchains>>;
    };
  };

  export function isStakingWalletField(
    f: Field<string>
  ): f is StakingWalletField {
    return isSelectField(f) && f.field === "stakingWallet";
  }

  export type DeclarationStatementsField = Switch<"policies.declarationStatements"> & {
    field: "policies.declarationStatements";
    desc: Record<string, string>;
  };

  export function isDeclarationStatementsField(
    f: Field<string>
  ): f is DeclarationStatementsField {
    return f.field === "policies.declarationStatements";
  }

  export type FinalDeclarationField = Switch<"policies.finalDeclaration"> & {
    field: "policies.finalDeclaration";
    desc: Record<string, string>;
  };

  export function isFinalDeclarationField(
    f: Field<string>
  ): f is FinalDeclarationField {
    return f.field === "policies.finalDeclaration";
  }

  export type ResideSanctionedCountryField = Switch<"policies.finalDeclaration"> & {
    field: "policies.finalDeclaration";
    desc: Record<string, string>;
  };

  export function isResideSanctionedCountryField(
    f: Field<string>
  ): f is ResideSanctionedCountryField {
    return f.field === "regulations.resideSanctionedCountry";
  }

  export type SekuritanceStatementsField = Switch<"policies.sekuritanceStatements"> & {
    field: "policies.sekuritanceStatements";
    desc: Record<string, string>;
  };

  export function isSekuritanceStatementsField(
    f: Field<string>
  ): f is SekuritanceStatementsField {
    return f.field === "policies.SekuritanceStatements";
  }

  export type UsPersonField = Switch<"regulations.usPerson"> & {
    field: "regulations.usPerson";
    desc: Record<string, string>;
  };

  export function isUsPersonField(f: Field<string>): f is UsPersonField {
    return f.field === "regulations.usPerson";
  }

  export type WhitepaperReadField = Switch<"policies.whitepaperRead"> & {
    field: "policies.whitepaperRead";
    desc: Record<string, string>;
  };

  export function isWhitepaperReadField(
    f: Field<string>
  ): f is WhitepaperReadField {
    return f.field === "policies.whitepaperRead";
  }

  export type IdentityDocumentField = Select<"identityDocument"> & {
    field: "identityDocument";
  };

  export function isIdentityDocumentField(
    f: Field<string>
  ): f is IdentityDocumentField {
    return isSelectField(f) && f.field === "identityDocument";
  }

  export type DefaultField =
    | {
        field: DefaultFieldNames;
        desc: Record<string, string> | null;
        optional?: boolean;
        type?: string;
      }
    | AddressAttachmentField
    | DeclarationStatementsField
    | FinalDeclarationField
    | IdentityDocumentField
    | SekuritanceStatementsField
    | SourceWalletField
    | DestinationWalletField
    | PledgeField
    | StakingWalletField;

  export type Field<T extends string> =
    | Switch<T>
    | MultiSelect<T>
    | MultiCheckbox<T>
    | Date<T>
    | Select<T>
    | Number<T>
    | Radio<T>
    | Text<T>
    | File<T>
    | DefaultField;

  export function isDefaultField(f: Field<string>): f is DefaultField {
    return !!defaultFieldNames.includes(f.field as any);
  }

  export function isBasicField<T extends string>(
    f: Field<T>
  ): f is Exclude<Field<T>, DefaultField> {
    return (
      isSelectField(f) ||
      isMultiCheckboxField(f) ||
      isMultiSelectField(f) ||
      isRadioField(f) ||
      isTextField(f) ||
      isDateField(f) ||
      isSwitchField(f) ||
      isNumberField(f) ||
      isFileField(f)
    );
  }

  export function isField(f: any): f is Field<string> {
    if (typeof f !== "object") return false;
    const objectKeys = Object.keys(f);

    return intersection(objectKeys, ["title", "field", "desc"]).length >= 3;
  }
}
